// Google Maps Places — PlaceAutocompleteElement (Places API New) with legacy fallback.

const GOOGLE_MAPS_API_KEY = 'AIzaSyCm_WUAtNlj_1sqXhTJstf2hcvdZeu1IE8'

let placesLibraryPromise = null

function loadMapsBootstrap() {
  if (window.google?.maps?.importLibrary) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    if (document.querySelector('script[data-google-maps-bootstrap]')) {
      const waitForGoogle = setInterval(() => {
        if (window.google?.maps?.importLibrary) {
          clearInterval(waitForGoogle)
          resolve()
        }
      }, 50)
      return
    }

    const script = document.createElement('script')
    script.dataset.googleMapsBootstrap = 'true'
    script.async = true
    script.defer = true
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&loading=async&callback=googleMapsBootstrap`
    script.onerror = () => reject(new Error('Failed to load Google Maps API'))

    window.googleMapsBootstrap = () => resolve()
    document.head.appendChild(script)
  })
}

export function loadGooglePlacesLibrary() {
  if (!placesLibraryPromise) {
    placesLibraryPromise = loadMapsBootstrap().then(() =>
      window.google.maps.importLibrary('places'),
    )
  }
  return placesLibraryPromise
}

export function loadGooglePlacesScript(callback) {
  loadGooglePlacesLibrary()
    .then(() => callback())
    .catch((error) => console.error(error))
}

export function parseAddressComponents(addressComponents) {
  if (!addressComponents?.length) {
    return null
  }

  let streetNumber = ''
  let route = ''
  let city = ''
  let state = ''
  let postalCode = ''

  for (const component of addressComponents) {
    const types = component.types || []
    if (types.includes('street_number')) {
      streetNumber = component.longText || component.long_name || ''
    }
    if (types.includes('route')) {
      route = component.shortText || component.short_name || ''
    }
    if (types.includes('locality')) {
      city = component.longText || component.long_name || ''
    }
    if (types.includes('administrative_area_level_1')) {
      state = component.shortText || component.short_name || ''
    }
    if (types.includes('postal_code')) {
      postalCode = component.longText || component.long_name || ''
    }
  }

  return {
    street: [streetNumber, route].filter(Boolean).join(' '),
    city,
    state,
    postalCode,
  }
}

function parseLegacyPlaceAddress(place) {
  return parseAddressComponents(place?.address_components)
}

function attachLegacyPlaceAutocomplete(inputElement, options = {}) {
  const autocomplete = new window.google.maps.places.Autocomplete(inputElement, {
    componentRestrictions: { country: options.includedRegionCodes?.[0] || 'us' },
    fields: ['address_components', 'formatted_address'],
    types: ['address'],
  })

  const onPlaceChanged = () => {
    const parsed = parseLegacyPlaceAddress(autocomplete.getPlace())
    if (!parsed?.street) {
      options.onInvalidSelection?.()
      return
    }
    options.onPlaceSelected?.(parsed)
  }

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  autocomplete.addListener('place_changed', onPlaceChanged)
  inputElement.addEventListener('keydown', onKeyDown)

  return {
    destroy() {
      window.google.maps.event.clearInstanceListeners(autocomplete)
      inputElement.removeEventListener('keydown', onKeyDown)
    },
  }
}

async function mountPlaceAutocompleteElement(container, options = {}) {
  const { PlaceAutocompleteElement } = await loadGooglePlacesLibrary()

  const element = new PlaceAutocompleteElement({})
  element.placeholder = options.placeholder || 'Start typing your address...'
  element.includedRegionCodes = options.includedRegionCodes || ['us']
  element.noInputIcon = true

  // Look comes from .places-autocomplete-host in styles/inputs.css, so it can follow the input size.
  container.replaceChildren(element)

  const onSelect = async (event) => {
    try {
      const place = event.placePrediction.toPlace()
      await place.fetchFields({ fields: ['addressComponents'] })
      const parsed = parseAddressComponents(place.addressComponents)
      if (!parsed?.street) {
        options.onInvalidSelection?.()
        return
      }
      options.onPlaceSelected?.(parsed)
    } catch (error) {
      console.error('Place autocomplete selection failed:', error)
      options.onInvalidSelection?.()
    }
  }

  const onInput = () => {
    options.onValueChange?.(element.value ?? '')
    options.onInput?.()
  }
  const onFocusIn = () => options.onFocus?.()
  const onFocusOut = () => options.onBlur?.()
  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  element.addEventListener('gmp-select', onSelect)
  element.addEventListener('input', onInput)
  element.addEventListener('focusin', onFocusIn)
  element.addEventListener('focusout', onFocusOut)
  element.addEventListener('keydown', onKeyDown)

  return {
    mode: 'new',
    setValue(value) {
      if ('value' in element) {
        element.value = value || ''
      }
    },
    destroy() {
      element.removeEventListener('gmp-select', onSelect)
      element.removeEventListener('input', onInput)
      element.removeEventListener('focusout', onFocusOut)
      element.removeEventListener('keydown', onKeyDown)
      element.removeEventListener('focusin', onFocusIn)
      container.replaceChildren()
    },
  }
}

async function mountLegacyPlaceAutocompleteInContainer(container, options = {}) {
  await loadGooglePlacesLibrary()

  container.replaceChildren()
  const input = document.createElement('input')
  input.type = 'text'
  input.spellcheck = false
  input.placeholder = options.placeholder || 'Start typing your address...'
  input.className = options.inputClass || 'input--text'
  if (options.initialValue) {
    input.value = options.initialValue
  }
  container.appendChild(input)

  input.addEventListener('input', (event) => {
    options.onValueChange?.(event.target.value)
    options.onInput?.()
  })
  input.addEventListener('focusout', () => options.onBlur?.())
  input.addEventListener('focusin', () => options.onFocus?.())

  const legacyHandle = attachLegacyPlaceAutocomplete(input, options)

  return {
    mode: 'legacy',
    setValue(value) {
      input.value = value || ''
    },
    destroy() {
      legacyHandle.destroy()
      container.replaceChildren()
    },
  }
}

/**
 * Mount address autocomplete (PlaceAutocompleteElement preferred, legacy fallback).
 */
export async function mountAddressAutocomplete(container, options = {}) {
  try {
    return await mountPlaceAutocompleteElement(container, options)
  } catch (error) {
    console.warn('PlaceAutocompleteElement unavailable, using legacy Autocomplete:', error)
    return mountLegacyPlaceAutocompleteInContainer(container, options)
  }
}

export async function ensureGoogleMapsLoaded() {
  await loadMapsBootstrap()
  return window.google.maps
}

const POLYGON_DRAW_STYLE = {
  fillColor: '#FFFF00',
  fillOpacity: 0.25,
  strokeColor: '#FFD700',
  strokeWeight: 2,
  editable: true,
}

const POLYGON_PREVIEW_STYLE = {
  strokeColor: '#FFD700',
  strokeWeight: 2,
  strokeOpacity: 0.9,
}

function getPolygonCoordinates(polygon) {
  const path = polygon.getPath()
  const coordinates = []
  for (let i = 0; i < path.getLength(); i++) {
    const point = path.getAt(i)
    coordinates.push({ lat: point.lat(), lng: point.lng() })
  }
  return coordinates
}

export async function attachPolygonDrawing(
  map,
  { onPolygonComplete, onPolygonClear, onDrawingStateChange, onDraftPointCountChange } = {},
) {
  await ensureGoogleMapsLoaded()

  let activePolygon = null
  let previewPolyline = null
  let draftVertices = []
  let isDrawing = false
  let pathListeners = []
  let mapClickListener = null

  const notifyDrawingState = () => {
    onDrawingStateChange?.(isDrawing)
  }

  const notifyDraftCount = () => {
    onDraftPointCountChange?.(draftVertices.length)
  }

  const clearPathListeners = () => {
    pathListeners.forEach((listener) => {
      window.google.maps.event.removeListener(listener)
    })
    pathListeners = []
  }

  const emitPolygonCoordinates = (polygon) => {
    onPolygonComplete?.(getPolygonCoordinates(polygon))
  }

  const watchPolygonPath = (polygon) => {
    clearPathListeners()
    const path = polygon.getPath()
    const events = ['set_at', 'insert_at', 'remove_at']
    pathListeners = events.map((eventName) =>
      path.addListener(eventName, () => emitPolygonCoordinates(polygon)),
    )
  }

  const clearPreview = () => {
    previewPolyline?.setMap(null)
    previewPolyline = null
  }

  const updatePreview = () => {
    clearPreview()
    if (draftVertices.length < 2) {
      return
    }
    previewPolyline = new window.google.maps.Polyline({
      map,
      path: draftVertices,
      ...POLYGON_PREVIEW_STYLE,
    })
  }

  const removeDrawingListener = () => {
    if (mapClickListener) {
      window.google.maps.event.removeListener(mapClickListener)
      mapClickListener = null
    }
  }

  const resetMapCursor = () => {
    map.setOptions({ draggableCursor: null, draggingCursor: null, disableDoubleClickZoom: false })
  }

  const clearPolygon = () => {
    stopDrawing()
    clearPathListeners()
    activePolygon?.setMap(null)
    activePolygon = null
    onPolygonClear?.()
  }

  const stopDrawing = () => {
    if (!isDrawing) {
      return
    }
    isDrawing = false
    removeDrawingListener()
    resetMapCursor()
    clearPreview()
    draftVertices = []
    notifyDraftCount()
    notifyDrawingState()
  }

  const finishDrawing = () => {
    if (!isDrawing || draftVertices.length < 3) {
      return false
    }

    const path = draftVertices.map((vertex) => ({ lat: vertex.lat(), lng: vertex.lng() }))
    stopDrawing()
    clearPathListeners()
    activePolygon?.setMap(null)

    activePolygon = new window.google.maps.Polygon({
      map,
      paths: path,
      ...POLYGON_DRAW_STYLE,
    })
    emitPolygonCoordinates(activePolygon)
    watchPolygonPath(activePolygon)
    return true
  }

  const startDrawing = () => {
    clearPolygon()
    isDrawing = true
    draftVertices = []
    notifyDraftCount()
    notifyDrawingState()
    map.setOptions({
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair',
      disableDoubleClickZoom: true,
    })

    mapClickListener = map.addListener('click', (event) => {
      if (!isDrawing) {
        return
      }
      draftVertices.push(event.latLng)
      notifyDraftCount()
      updatePreview()
    })
  }

  return {
    startDrawing,
    finishDrawing,
    stopDrawing,
    clearPolygon,
    destroy() {
      clearPolygon()
    },
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildDonationMapInfoContent(location) {
  const easternOptions = { timeZone: 'America/New_York' }
  const createdAt = location.datetimeCreated ? new Date(location.datetimeCreated) : null
  const pledgeDate = createdAt
    ? createdAt.toLocaleDateString('en-US', {
        ...easternOptions,
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      })
    : '-'
  const signupTime = createdAt
    ? createdAt.toLocaleTimeString('en-US', {
        ...easternOptions,
        hour: 'numeric',
        minute: '2-digit',
      })
    : '-'

  const rows = [
    ['Pledge ID:', location.givarooId],
    ['Campaign:', location.campaignName],
    ['Pledge Date:', pledgeDate],
    ['Fundraiser:', location.fundraiserName],
    ['Market:', location.campaignLocation],
    ['Vendor:', location.canvassingOrgName],
    ['Time at Signup:', signupTime],
    ['Donor Age:', location.donorAge ?? '-'],
    ['Status:', location.currentDonorStatus ?? '-'],
    ['Payments Before Cancellation:', location.paymentsBeforeCancellation ?? '-'],
    ['Record Source:', location.recordSource],
  ]

  const body = rows
    .map(
      ([label, value]) =>
        `<div style="display:flex;gap:12px;margin-bottom:6px;align-items:flex-start"><div style="flex:0 0 52%;font-weight:600;color:#111827;line-height:1.35;min-width:0;overflow-wrap:break-word">${escapeHtml(
          label,
        )}</div><div style="flex:1 1 48%;color:#374151;line-height:1.35;min-width:0;overflow-wrap:break-word">${escapeHtml(
          value,
        )}</div></div>`,
    )
    .join('')

  return `<div style="font-family:system-ui,sans-serif;font-size:13px;line-height:1.4;box-sizing:border-box;width:100%;padding:2px 0;color:#374151">${body}</div>`
}

function getDonationInfoWindowMaxWidth() {
  if (typeof window === 'undefined') {
    return 380
  }
  return Math.min(400, Math.max(280, window.innerWidth - 32))
}

function getMapBounds(locations) {
  const bounds = new window.google.maps.LatLngBounds()
  locations.forEach((location) => {
    bounds.extend({ lat: Number(location.latitude), lng: Number(location.longitude) })
  })
  return bounds
}

const MAP_BOUNDS_PADDING = { top: 48, right: 48, bottom: 48, left: 48 }

function triggerMapResize(map) {
  if (!map || !window.google?.maps?.event) {
    return
  }
  window.google.maps.event.trigger(map, 'resize')
}

function refitMapToLocations(map, locations) {
  if (!map || !locations?.length) {
    return
  }

  triggerMapResize(map)

  if (locations.length === 1) {
    map.setCenter({
      lat: Number(locations[0].latitude),
      lng: Number(locations[0].longitude),
    })
    map.setZoom(14)
    return
  }

  map.fitBounds(getMapBounds(locations), MAP_BOUNDS_PADDING)
}

const DONATION_MARKER_ICON_ACTIVE = 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
const DONATION_MARKER_ICON_INACTIVE = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'

function getDonationMarkerIcon(currentDonorStatusValue) {
  const status = String(currentDonorStatusValue || '').toLowerCase()
  const iconUrl = status === 'active' ? DONATION_MARKER_ICON_ACTIVE : DONATION_MARKER_ICON_INACTIVE
  return { url: iconUrl }
}

export async function renderDonationLocationMarkers(map, locations, { onMarkersChange } = {}) {
  await ensureGoogleMapsLoaded()
  const infoWindow = new window.google.maps.InfoWindow({
    maxWidth: getDonationInfoWindowMaxWidth(),
  })
  const markers = locations.map((location) => {
    const marker = new window.google.maps.Marker({
      map,
      position: { lat: Number(location.latitude), lng: Number(location.longitude) },
      title: String(location.givarooId ?? ''),
      icon: getDonationMarkerIcon(location.currentDonorStatusValue),
    })
    marker.addListener('click', () => {
      infoWindow.close()
      infoWindow.setOptions({ maxWidth: getDonationInfoWindowMaxWidth() })
      infoWindow.setContent(buildDonationMapInfoContent(location))
      infoWindow.open({ map, anchor: marker })
    })
    return marker
  })

  if (markers.length) {
    refitMapToLocations(map, locations)
  }

  onMarkersChange?.(markers, infoWindow)
  return { markers, infoWindow }
}

export async function createDonationLocationsMap(container, locations = []) {
  await ensureGoogleMapsLoaded()
  const { Map } = await window.google.maps.importLibrary('maps')

  const map = new Map(container, {
    center: { lat: 33.749, lng: -84.388 },
    zoom: 10,
    mapTypeId: 'hybrid',
  })

  let markers = []
  let infoWindow = null
  let currentLocations = []
  let resizeFrame = null
  const mapClickListener = map.addListener('click', () => {
    infoWindow?.close()
  })

  const scheduleRefit = () => {
    if (!currentLocations.length) {
      return
    }
    if (resizeFrame) {
      cancelAnimationFrame(resizeFrame)
    }
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = null
      refitMapToLocations(map, currentLocations)
    })
  }

  const resizeObserver = new ResizeObserver(() => {
    scheduleRefit()
  })
  resizeObserver.observe(container)

  const onWindowResize = () => scheduleRefit()
  window.addEventListener('resize', onWindowResize)

  const clearMarkers = () => {
    markers.forEach((marker) => marker.setMap(null))
    markers = []
    infoWindow?.close()
  }

  const updateLocations = async (nextLocations) => {
    currentLocations = nextLocations ?? []
    clearMarkers()
    if (!currentLocations.length) {
      return
    }
    const result = await renderDonationLocationMarkers(map, currentLocations, {
      onMarkersChange: (nextMarkers, nextInfoWindow) => {
        markers = nextMarkers
        infoWindow = nextInfoWindow
      },
    })
    markers = result.markers
    infoWindow = result.infoWindow
    scheduleRefit()
  }

  await updateLocations(locations)

  return {
    map,
    updateLocations,
    refit: () => scheduleRefit(),
    attachDrawing: (options) => attachPolygonDrawing(map, options),
    destroy() {
      if (resizeFrame) {
        cancelAnimationFrame(resizeFrame)
      }
      resizeObserver.disconnect()
      window.removeEventListener('resize', onWindowResize)
      window.google.maps.event.removeListener(mapClickListener)
      clearMarkers()
    },
  }
}
